import { useEffect, useMemo, useState } from "react";
import type { PrintingType } from "../../../../../models/ProjectManagement/PrintingType";
import { useStores } from "../../../use-stores";

/**
 * Printing-type selection and template-required checkbox for mockup generation.
 * Printing-file upload lives outside the generator modal
 * (sourcing ops for orders, Swagstore product card for stores).
 */
export function usePrintingConfig({
  open,
  productId,
}: {
  open: boolean;
  productId?: number;
}) {
  const { projectSourcingOpsStore } = useStores();
  const [printingTypes, setPrintingTypes] = useState<PrintingType[]>([]);
  const [loadingPrintingTypes, setLoadingPrintingTypes] = useState(false);
  const [selectedPrintingType, setSelectedPrintingType] = useState("");
  const [templateRequired, setTemplateRequired] = useState(false);

  useEffect(() => {
    if (!productId) {
      setPrintingTypes([]);
      setSelectedPrintingType("");
      return;
    }

    let cancelled = false;
    setLoadingPrintingTypes(true);
    void projectSourcingOpsStore
      .getTypesOfPrinting(Number(productId))
      .then((types: PrintingType[] | undefined) => {
        if (cancelled) return;
        const activeTypes = (types ?? []).filter((type) => type.active !== false);
        setPrintingTypes(activeTypes);
        setSelectedPrintingType((current) => {
          if (current && activeTypes.some((type) => type.type === current)) {
            return current;
          }
          return activeTypes.length === 1 ? activeTypes[0].type : "";
        });
      })
      .catch(() => {
        if (!cancelled) {
          setPrintingTypes([]);
          setSelectedPrintingType("");
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingPrintingTypes(false);
      });

    return () => {
      cancelled = true;
    };
  }, [productId, projectSourcingOpsStore]);

  useEffect(() => {
    if (!open) setTemplateRequired(false);
  }, [open]);

  const printingTypeOptions = useMemo(
    () =>
      printingTypes.map((type) => ({
        id: type.type,
        value: type.type,
      })),
    [printingTypes],
  );

  return {
    printingTypes,
    loadingPrintingTypes,
    selectedPrintingType,
    setSelectedPrintingType,
    printingTypeOptions,
    templateRequired,
    setTemplateRequired,
  };
}
